import { useState, useMemo } from "react";
import { Wallet, Fingerprint, Radio, Eye, EyeOff, ScanLine, CalendarDays, X } from "lucide-react";
import { C } from "../../theme/tokens";
import { SectionHeader, TicketCard, Chip } from "../../components/common/primitives";
import { CollapsibleSection } from "../../components/common/CollapsibleSection";
import { StatsLedger } from "./StatsLedger";
import { SignalHistoryRow } from "./SignalHistoryRow";
import { VipLinksCard } from "./VipLinksCard";
import { AccountSecurityCard } from "./AccountSecurityCard";
import { useVipLinks } from "../../hooks/useVipLinks";
import { useHiddenBalance } from "../../hooks/useHiddenBalance";
import { TRIAL_SIGNAL_LIMIT, TRIAL_SIGNAL_BUCKETS, getTrialSignalsSnapshot } from "../../access/sectionAccess";

/* Compact "filter by day" control used in the signal-history section —
   a native <input type="date"> keeps it accessible and avoids pulling
   in a full calendar-picker component for one field. Value is the
   plain "YYYY-MM-DD" string the input already produces, compared
   as-is against each signal's local date key in ProfileScreen. */
function DateFilterControl({ value, onChange, t }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: C.panel1,
        border: `1px solid ${value ? C.violetBright : C.hairline}`,
        borderRadius: 10,
        padding: "6px 10px",
        flexShrink: 0,
      }}
    >
      <CalendarDays size={13} color={value ? C.violetBright : C.mutedB} />
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={t.prof_filter_date_all}
        style={{
          background: "transparent",
          border: "none",
          outline: "none",
          color: value ? C.primary : C.mutedB,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12.5,
          colorScheme: "dark",
          maxWidth: 120,
        }}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={t.prof_filter_date_clear}
          title={t.prof_filter_date_clear}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 18,
            height: 18,
            padding: 0,
            border: "none",
            background: "transparent",
            color: C.mutedB,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}

export function ProfileScreen({
  t,
  lang,
  setLang,
  wide,
  user,
  stats,
  authFetch,
  requestTelegramLinkCode,
  setPassword,
  linkEmail,
  onLogout,
}) {
  // Trading-account ID: Pocket users have pocket_id, BabaOption users have babaoption_id.
  const hasPocketId = user?.pocket_id != null && user.pocket_id !== "";
  const hasBabaId = !hasPocketId && user?.babaoption_id != null && user.babaoption_id !== "";
  const accountIdLabel = hasBabaId ? t.prof_id_baba : t.prof_id;
  const accountId = hasPocketId ? String(user.pocket_id) : hasBabaId ? String(user.babaoption_id) : "—";
  const balance = typeof user?.balance === "number" ? user.balance : 0;
  const [filter, setFilter] = useState("all"); // all | win | loss
  const [sourceFilter, setSourceFilter] = useState("all"); // all | SIGNALS | SCANNER
  const [dateFilter, setDateFilter] = useState(""); // "" | "YYYY-MM-DD"
  const { status: vipStatus, vipChat, vipChannel, generate: generateVipLinks } = useVipLinks(authFetch);
  const [balanceHidden, toggleBalanceHidden] = useHiddenBalance();

  // Trial users see their remaining signal quota instead of a balance.
  // The trial is metered by usage now (5 signals per bucket) rather
  // than by a wall-clock expiry, and the backend is the source of
  // truth for whether it's still active.
  const isTrial = user?.is_trial === true;
  const trialExpired = isTrial && user?.trial_active !== true;
  const trialSnapshot = isTrial ? getTrialSignalsSnapshot(user) : {};
  // Flatten { signals: { S5: 5, ... }, scanner: { "": 5 } } into a
  // display list, skipping buckets the backend hasn't sent yet.
  const trialChips = Object.entries(TRIAL_SIGNAL_BUCKETS).flatMap(([section, dealTypes]) =>
    dealTypes
      .filter((dealType) => typeof trialSnapshot?.[section]?.[dealType] === "number")
      .map((dealType) => ({
        key: `${section}:${dealType}`,
        label: dealType || (section === "scanner" ? t.prof_trial_scanner : section),
        remaining: trialSnapshot[section][dealType],
      }))
  );
  const trialTotalRemaining = trialChips.reduce((sum, c) => sum + c.remaining, 0);

  // Local (not UTC) Y-M-D key for a signal's timestamp, so it lines up
  // with the plain "YYYY-MM-DD" string a native <input type="date">
  // hands back — both compared as strings, no timezone re-parsing.
  const toDateKey = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Source + date narrow down which signals count toward the stats
  // tiles (winrate / total) as well as the list below — so picking a
  // day or a bot shows "statistics for that slice", not just a
  // filtered list under unrelated totals.
  const scoped = useMemo(() => {
    const history = Array.isArray(stats) ? stats : [];
    return history.filter((s) => {
      if (sourceFilter !== "all" && s.source !== sourceFilter) return false;
      if (dateFilter && toDateKey(s.created_at) !== dateFilter) return false;
      return true;
    });
  }, [stats, sourceFilter, dateFilter]);

  const wins = scoped.filter((s) => s.profit === true).length;
  const winrate = scoped.length ? Math.round((wins / scoped.length) * 100) : 0;
  const filtered = scoped.filter((s) =>
    filter === "all" ? true : filter === "win" ? s.profit === true : s.profit === false
  );
  const hasActiveFilters = filter !== "all" || sourceFilter !== "all" || !!dateFilter;

  return (
    <div>
      <SectionHeader title={t.prof_title} wide={wide} />

      <div
        style={{
          padding: wide ? "4px 0 20px" : "4px 20px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          maxWidth: wide ? 640 : undefined,
          margin: wide ? "0 auto" : undefined,
          width: "100%",
        }}
      >
        {/* balance / trial */}
        <CollapsibleSection title={isTrial ? t.prof_trial : t.prof_balance} storageKey="balance">
          <TicketCard>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "flex-start",
                columnGap: 12,
                rowGap: 16,
              }}
            >
              <div style={{ minWidth: 0, flex: "1 1 200px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: C.mutedB,
                    fontSize: 11.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  <Wallet size={13} /> {isTrial ? t.prof_trial : t.prof_balance}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 10,
                    marginTop: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "clamp(22px, 5vw, 30px)",
                      fontWeight: 700,
                      color: C.primary,
                      wordBreak: "break-all",
                    }}
                  >
                    {isTrial
                      ? (trialExpired
                          ? t.prof_trial_expired
                          : trialChips.length > 0
                            ? (
                              <>
                                {trialTotalRemaining}
                                <span style={{ fontSize: 16, color: C.mutedB }}> {t.prof_trial_signals_left}</span>
                              </>
                            )
                            : t.prof_trial)
                      : balanceHidden
                        ? balance.toFixed(2).replace(/[0-9]/g, "*")
                        : (
                          <>
                            {balance.toFixed(2)} <span style={{ fontSize: 16, color: C.mutedB }}>USD</span>
                          </>
                        )}
                  </div>

                  {!isTrial && (
                    <button
                      type="button"
                      onClick={toggleBalanceHidden}
                      aria-label={balanceHidden ? t.prof_balance_show : t.prof_balance_hide}
                      title={balanceHidden ? t.prof_balance_show : t.prof_balance_hide}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 26,
                        height: 26,
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        color: C.mutedB,
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      {balanceHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  )}
                </div>
              </div>

              <div style={{ textAlign: "right", flex: "0 0 auto" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 6,
                    color: C.mutedB,
                    fontSize: 11.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {accountIdLabel} <Fingerprint size={13} />
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 14.5,
                    fontWeight: 700,
                    color: C.primary,
                    marginTop: 8,
                    letterSpacing: "0.02em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {accountId}
                </div>
              </div>
            </div>

            {isTrial && !trialExpired && trialChips.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
                {trialChips.map(({ key, label, remaining }) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "5px 9px",
                      borderRadius: 8,
                      background: C.panel1,
                      border: `1px solid ${C.hairline}`,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 12,
                      color: C.secondary,
                    }}
                  >
                    <span style={{ color: C.primary, fontWeight: 700 }}>{label}</span>
                    {remaining}/{TRIAL_SIGNAL_LIMIT}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 2, paddingTop: 14 }} />
          </TicketCard>
        </CollapsibleSection>

        {/* stats */}
        <CollapsibleSection title={t.prof_stats} storageKey="stats">
          <StatsLedger
            winrate={winrate}
            winrateLabel={t.prof_winrate}
            totalSignals={scoped.length}
            totalLabel={t.prof_total_signals}
          />
        </CollapsibleSection>

        {/* VIP chat / channel links */}
        <CollapsibleSection title={t.prof_vip_title} storageKey="vip">
          <VipLinksCard
            t={t}
            status={vipStatus}
            vipChat={vipChat}
            vipChannel={vipChannel}
            onGenerate={generateVipLinks}
            trialLocked={isTrial && !trialExpired}
          />
        </CollapsibleSection>

        {/* Account Management & Security: language, telegram link, password, logout */}
        <CollapsibleSection title={t.prof_security_title} storageKey="security">
          <AccountSecurityCard
            t={t}
            lang={lang}
            setLang={setLang}
            user={user}
            requestTelegramLinkCode={requestTelegramLinkCode}
            setPassword={setPassword}
            linkEmail={linkEmail}
            onLogout={onLogout}
          />
        </CollapsibleSection>

        {/* signal history */}
        <CollapsibleSection title={t.prof_history} storageKey="history">
          <div>
            <div style={{ fontSize: 12, color: C.mutedB, marginTop: -2, marginBottom: 12 }}>{t.prof_history_sub}</div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              <Chip active={filter === "all"} onClick={() => setFilter("all")}>
                {t.prof_filter_all}
              </Chip>
              <Chip active={filter === "win"} onClick={() => setFilter("win")}>
                {t.prof_filter_win}
              </Chip>
              <Chip active={filter === "loss"} onClick={() => setFilter("loss")}>
                {t.prof_filter_loss}
              </Chip>
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <Chip active={sourceFilter === "all"} onClick={() => setSourceFilter("all")}>
                  {t.prof_filter_source_all}
                </Chip>
                <Chip active={sourceFilter === "SIGNALS"} onClick={() => setSourceFilter("SIGNALS")}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <Radio size={12} /> {t.prof_filter_signals}
                  </span>
                </Chip>
                <Chip active={sourceFilter === "SCANNER"} onClick={() => setSourceFilter("SCANNER")}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <ScanLine size={12} /> {t.prof_filter_scanner}
                  </span>
                </Chip>
              </div>
              <DateFilterControl value={dateFilter} onChange={setDateFilter} t={t} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: C.mutedB,
                    fontSize: 13,
                    padding: "24px 12px",
                    background: C.panel1,
                    border: `1px solid ${C.hairline}`,
                    borderRadius: 14,
                  }}
                >
                  {hasActiveFilters ? t.prof_history_empty_filtered : t.prof_history_empty}
                </div>
              ) : (
                filtered.map((item) => (
                  <SignalHistoryRow key={item.id} item={item} t={t} lang={lang} />
                ))
              )}
            </div>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
